import { projectsId, type ProjectsId } from "./Projects";
import { environmentsId, type EnvironmentsId } from "./Environments";
import { notificationLogsId, type NotificationLogsId } from "./NotificationLogs";
import { recipientType, type default as RecipientType } from "./RecipientType";
import type { ColumnType, Selectable, Insertable, Updateable } from "kysely";
import { z } from "zod";

/** Identifier type for virtual_inbox_messages */
export type VirtualInboxMessagesId = string & { __flavor: "VirtualInboxMessagesId" };

/**
 * Represents the table public.virtual_inbox_messages
 * @uniqueConstraints {"virtual_inbox_messages_pkey":{"name":"virtual_inbox_messages_pkey","isPrimary":true,"isUnique":true,"columns":[{"name":"id","definition":"id","predicate":null}]},"virtual_inbox_messages_project_idx":{"name":"virtual_inbox_messages_project_idx","isPrimary":false,"isUnique":false,"columns":[{"name":"project_id","definition":"project_id","predicate":null}]},"virtual_inbox_messages_environment_idx":{"name":"virtual_inbox_messages_environment_idx","isPrimary":false,"isUnique":false,"columns":[{"name":"environment_id","definition":"environment_id","predicate":null}]},"virtual_inbox_messages_created_at_idx":{"name":"virtual_inbox_messages_created_at_idx","isPrimary":false,"isUnique":false,"columns":[{"name":"created_at","definition":"created_at","predicate":null}]}}
 */
export default interface VirtualInboxMessagesTable {
  id: ColumnType<
    VirtualInboxMessagesId,
    VirtualInboxMessagesId | undefined,
    VirtualInboxMessagesId
  >;

  projectId: ColumnType<ProjectsId, ProjectsId, ProjectsId>;

  environmentId: ColumnType<EnvironmentsId, EnvironmentsId, EnvironmentsId>;

  notificationLogId: ColumnType<
    NotificationLogsId | null,
    NotificationLogsId | null,
    NotificationLogsId | null
  >;

  recipient: ColumnType<string, string, string>;

  recipientType: ColumnType<RecipientType, RecipientType, RecipientType>;

  subject: ColumnType<string | null, string | null, string | null>;

  providerName: ColumnType<string | null, string | null, string | null>;

  mailpitMessageId: ColumnType<string | null, string | null, string | null>;

  payload: ColumnType<unknown | null, unknown | null, unknown | null>;

  createdAt: ColumnType<Date, Date | string | undefined, Date | string>;

  readAt: ColumnType<Date | null, Date | string | null, Date | string | null>;
}

export type VirtualInboxMessages = Selectable<VirtualInboxMessagesTable>;

export type NewVirtualInboxMessages = Insertable<VirtualInboxMessagesTable>;

export type VirtualInboxMessagesUpdate = Updateable<VirtualInboxMessagesTable>;

export const virtualInboxMessagesId = z.string() as unknown as z.Schema<VirtualInboxMessagesId>;

export const virtualInboxMessages = z.object({
  id: virtualInboxMessagesId,
  projectId: projectsId,
  environmentId: environmentsId,
  notificationLogId: notificationLogsId.nullable(),
  recipient: z.string(),
  recipientType: recipientType,
  subject: z.string().nullable(),
  providerName: z.string().nullable(),
  mailpitMessageId: z.string().nullable(),
  payload: z.unknown().nullable(),
  createdAt: z.date(),
  readAt: z.date().nullable(),
});

export const virtualInboxMessagesInitializer = z.object({
  id: virtualInboxMessagesId.optional(),
  projectId: projectsId,
  environmentId: environmentsId,
  notificationLogId: notificationLogsId.optional().nullable(),
  recipient: z.string(),
  recipientType: recipientType,
  subject: z.string().optional().nullable(),
  providerName: z.string().optional().nullable(),
  mailpitMessageId: z.string().optional().nullable(),
  payload: z.unknown().optional().nullable(),
  createdAt: z.date().optional(),
  readAt: z.date().optional().nullable(),
});

export const virtualInboxMessagesMutator = z.object({
  id: virtualInboxMessagesId.optional(),
  projectId: projectsId.optional(),
  environmentId: environmentsId.optional(),
  notificationLogId: notificationLogsId.optional().nullable(),
  recipient: z.string().optional(),
  recipientType: recipientType.optional(),
  subject: z.string().optional().nullable(),
  providerName: z.string().optional().nullable(),
  mailpitMessageId: z.string().optional().nullable(),
  payload: z.unknown().optional().nullable(),
  createdAt: z.date().optional(),
  readAt: z.date().optional().nullable(),
});

// ── constraints for VirtualInboxMessages ─────────────────────────────────
export const VirtualInboxMessagesConstraints = {
  virtual_inbox_messages_pkey: {
    name: "virtual_inbox_messages_pkey",
    isPrimary: true,
    isUnique: true,
    columns: [{ name: "id", definition: "id", predicate: null }],
  },
  virtual_inbox_messages_project_idx: {
    name: "virtual_inbox_messages_project_idx",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "project_id", definition: "project_id", predicate: null }],
  },
  virtual_inbox_messages_environment_idx: {
    name: "virtual_inbox_messages_environment_idx",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "environment_id", definition: "environment_id", predicate: null }],
  },
  virtual_inbox_messages_created_at_idx: {
    name: "virtual_inbox_messages_created_at_idx",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "created_at", definition: "created_at", predicate: null }],
  },
} as const;
export type VirtualInboxMessagesConstraintsType = typeof VirtualInboxMessagesConstraints;
